import { IApiError } from '@frontend/request';

import {
  IDealHistory,
  IGetDealHistoryParams,
  IGetOrderByIdParams,
  IGetOrderByIdV2Params,
  IGetOrderBySymbolParams,
  IGetOrderCountsResponse,
  IGetOrderHistoryParams,
  IOrder,
  IOrderHistory,
  IPendingOrder,
  orderAPI
} from '@aqx-core/common/api/order';
import { IAPIControllerCallbacks } from '@aqx-core/common/api/types';
import { companyService } from '@aqx-core/common/services/company';

import { EMainProductCodeType } from '../../config/model/types';

class OrderAPIController {
  private openPositionsAC?: AbortController;
  private openAndPendingOrderCountsAC?: AbortController;
  private pendingOrdersAC?: AbortController;
  private orderHistoriesAC?: AbortController;
  private dealHistoriesAC?: AbortController;

  public getAllOpenPositions = async (
    params: IGetOrderBySymbolParams,
    callbacks: IAPIControllerCallbacks<IOrder[]>
  ) => {
    try {
      this.openPositionsAC?.abort();
      this.openPositionsAC = new AbortController();

      const { result } = await orderAPI.getAllOpenPositions(params, this.openPositionsAC.signal);
      callbacks.onSuccess(result);
    } catch (error) {
      callbacks.onError(error as IApiError);
    } finally {
      callbacks.onFinally?.();
    }
  };

  public getOpenAndPendingOrderCounts = async (
    params: IGetOrderBySymbolParams,
    callbacks: IAPIControllerCallbacks<IGetOrderCountsResponse>
  ) => {
    try {
      this.openAndPendingOrderCountsAC?.abort();
      this.openAndPendingOrderCountsAC = new AbortController();

      const { result } = await orderAPI.getOpenAndPendingOrderCounts(params, this.openAndPendingOrderCountsAC.signal);
      callbacks.onSuccess(result);
    } catch (error) {
      callbacks.onError(error as IApiError);
    }
  };

  public getDealHistories = async (
    params: IGetDealHistoryParams,
    callbacks: IAPIControllerCallbacks<IDealHistory[]>
  ) => {
    try {
      this.dealHistoriesAC?.abort();
      this.dealHistoriesAC = new AbortController();

      const { result } = await orderAPI.getDealHistories(params, this.dealHistoriesAC.signal);
      callbacks.onSuccess(result);
    } catch (error) {
      callbacks.onError(error as IApiError);
    } finally {
      callbacks.onFinally?.();
    }
  };

  public getOrderDetail = async (params: IGetOrderByIdParams, callbacks: IAPIControllerCallbacks<IOrder>) => {
    try {
      const { result } = await orderAPI.getOrderDetail(params);
      callbacks.onSuccess(result);
    } catch (error) {
      callbacks.onError(error as IApiError);
    } finally {
      callbacks.onFinally?.();
    }

    callbacks.onFinally?.();
  };

  public getOrderDetailV2 = async (params: IGetOrderByIdV2Params, callbacks: IAPIControllerCallbacks<IOrder>) => {
    try {
      const { result } = await orderAPI.getOrderDetailV2(params);
      callbacks.onSuccess(result);
    } catch (error) {
      callbacks.onError(error as IApiError);
    } finally {
      callbacks.onFinally?.();
    }
  };

  public getOrderHistories = async (
    params: IGetOrderHistoryParams,
    callbacks: IAPIControllerCallbacks<IOrderHistory[]>
  ) => {
    try {
      this.orderHistoriesAC?.abort();
      this.orderHistoriesAC = new AbortController();
      const companyIntegration = companyService.getCompanyIntegration();

      if (companyIntegration === EMainProductCodeType.HORIZON) {
        const { result } = await orderAPI.getOrderHistories(params, this.orderHistoriesAC.signal);
        callbacks.onSuccess(result);
      } else {
        const { result } = await orderAPI.getOrderHistoriesV3(params, this.orderHistoriesAC.signal);
        callbacks.onSuccess(result);
      }
    } catch (error) {
      callbacks.onError(error as IApiError);
    } finally {
      callbacks.onFinally?.();
    }
  };

  public getAllPendingOrders = async (
    params: IGetOrderBySymbolParams,
    callbacks: IAPIControllerCallbacks<IPendingOrder[]>
  ) => {
    try {
      this.pendingOrdersAC?.abort();
      this.pendingOrdersAC = new AbortController();

      const { result } = await orderAPI.getAllPendingOrders(params, this.pendingOrdersAC.signal);
      callbacks.onSuccess(result);
    } catch (error) {
      callbacks.onError(error as IApiError);
    } finally {
      callbacks.onFinally?.();
    }
  };

  public getPendingOrderDetail = async (
    params: IGetOrderByIdParams,
    callbacks: IAPIControllerCallbacks<IPendingOrder>
  ) => {
    try {
      const { result } = await orderAPI.getPendingOrderDetail(params);
      callbacks.onSuccess(result);
    } catch (error) {
      callbacks.onError(error as IApiError);
    } finally {
      callbacks.onFinally?.();
    }
  };
}

export const orderAPIController = new OrderAPIController();
